import { ReactNode, useEffect, useRef } from "react";
import {
	NewOrderEarprintShippingType,
	NewOrderEarSide,
	NewOrderRenewReason,
	NewOrderStep,
	NewOrderType,
	useNewOrderStore,
} from "../stores/order.store";
import {
	Avatar,
	Box,
	BoxProps,
	Button,
	Flex,
	Grid,
	ListItem,
	Radio,
	Text,
	Textarea,
	UnorderedList,
	useDisclosure,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import {
	useCenter,
	useCenterAudio,
	useCenters,
	useCenterUsersPaginated,
} from "../hooks/centers.hooks";
import { User, UserCenterRole } from "../types/user.types";
import { AddIcon, CheckCircleIcon, CopyIcon } from "@chakra-ui/icons";
import Input from "../components/inputs/Input";
import { Center } from "../types/center.types";
import { Patient } from "../types/patient.types";
import { add, format } from "date-fns";
import SimpleModal from "../components/modals/SimpleModal";
import InviteUserForm, {
	InviteUserFormHandler,
} from "../components/forms/LinkUserForm";
import { useMutation, useQueryClient } from "react-query";
import { sendInvitations } from "../mutations/center.mutations";
import { useNavigate } from "react-router-dom";
import AddUserIcon from "../components/icons/AddUserIcon";
import PatientForm, {
	PatientFormHandler,
} from "../components/forms/PatientForm";
import { createPatient } from "../mutations/patient.mutations";
import {
	useEarmoldsPaginated,
	useEarmoldVariants,
} from "../hooks/earmolds.hooks";
import CircleHelpIcon from "../components/icons/CircleHelpIcon";
import Tooltip from "../components/Tooltip";
import SelectDropdown, {
	TSelectDropdownOption,
} from "../components/inputs/SelectDropdown";
import { useSimpleToast } from "../hooks/toast.hooks";
import { createFile } from "../mutations/file.mutations";
import { SavedFile } from "../types/files.types";
import LeftEarCard from "../components/LeftEarCard";
import RightEarCard from "../components/RightEarCard";
import DetailCard from "../components/DetailCard";
import PatientCard from "../components/PatientCard";
import CenterCard from "../components/CenterCard";
import FileInlineInput from "../components/FileInlineInput";
import FileInputButton from "../components/FileInputButton";
import SimpleSuccessFeedbackModal from "../components/modals/SimpleSuccessFeedbackModal";
import { CommandStatus } from "../types/command.types";
import { usePatientCommands } from "../hooks/patients.hooks";
import "react-day-picker/style.css";
import InputSingleDatePicker from "../components/inputs/InputSingleDatePicker";
import { useConnectedUser } from "../hooks/user.hooks";
import i18n from "../i18n";
import { useAdminSettings } from "../hooks/admin_settings.hooks";
import { Matcher } from "react-day-picker";
import { fetchEarmoldVariants } from "../queries/earmold.queries";
import PatientSelect from "../components/inputs/PatientSelect";
import CenterSelect from "../components/inputs/CenterSelect";
import { usePrintPageCommandPurchaseOrderFile } from "../hooks/printers.hooks";
import _ from "lodash";

const OrdersNewPageHeader = () => {
	const { getStep, goPreviousStep, closeWithoutSaving } = useNewOrderStore();
	const { saveDraft } = useNewOrderStore();
	const { isUpdate } = useNewOrderStore();
	const navigate = useNavigate();
	const toast = useSimpleToast();
	const queryClient = useQueryClient();

	const NavigationItem = ({
		children,
		active = false,
		first = false,
		last = false,
	}: {
		children: ReactNode;
		active?: boolean;
		first?: boolean;
		last?: boolean;
	}) => {
		return (
			<ListItem
				w="fit-content"
				px={4}
				py={2}
				borderRight={last ? undefined : "solid 1px #EEF5FF"}
				borderRightRadius={last ? "10rem" : undefined}
				borderLeft={first ? "solid 1px #EEF5FF" : undefined}
				borderLeftRadius={first ? "10rem" : undefined}
				bgColor={active ? "brand.700" : undefined}
				color={active ? "white" : undefined}
			>
				{children}
			</ListItem>
		);
	};

	return (
		<Flex
			px={4}
			h="70px"
			w="100%"
			bgColor="white"
			borderBottom="solid 1px"
			borderColor="gray.200"
			justifyContent="space-between"
			alignItems="center"
		>
			<Flex>
				<Button
					variant="ghost"
					onClick={() => {
						if (getStep() === 0) {
							navigate(-1);
							return;
						}
						goPreviousStep();
					}}
				>
					Retour
				</Button>
			</Flex>

			<UnorderedList
				display="flex"
				listStyleType="none"
				border="solid 1px"
				borderColor="gray.200"
				borderRadius="10rem"
			>
				<NavigationItem
					active={NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE <= getStep()}
					first
				>
					Informations générales
				</NavigationItem>
				<NavigationItem active={NewOrderStep.CHOOSE_COMMAND_TYPE <= getStep()}>
					Type de commande
				</NavigationItem>
				<NavigationItem active={NewOrderStep.CHOOSE_SETUP <= getStep()}>
					Caractéristiques des embouts
				</NavigationItem>
				<NavigationItem active={NewOrderStep.VALIDATION <= getStep()} last>
					Validation
				</NavigationItem>
			</UnorderedList>

			<Flex gap={4}>
				<Button
					variant={"outline"}
					onClick={() => {
						closeWithoutSaving();
						navigate(-1);
					}}
				>
					Fermer sans enregistrer
				</Button>
				{isUpdate() === true && (
					<Button
						variant="outline"
						onClick={async () => {
							try {
								saveDraft({ log: true });
								toast.success(
									"Brouillon",
									"La commande en cours a été sauvegardée",
								);
								await queryClient.invalidateQueries();
								navigate("/orders");
							} catch (err) {
								toast.error("Brouillon", "Erreur lors de la sauvegarde");
							}
						}}
					>
						Enregistrer les modifications
					</Button>
				)}
				{isUpdate() === false && (
					<Button
						variant="outline"
						onClick={async () => {
							try {
								saveDraft();
								toast.success(
									"Modification",
									"La commande en cours a été sauvegardée",
								);
								await queryClient.invalidateQueries();
								navigate(-1);
							} catch (err) {
								toast.error("Modification", "Erreur lors de la sauvegarde");
							}
						}}
					>
						Enregistrer en brouillon
					</Button>
				)}
			</Flex>
		</Flex>
	);
};

/**
 * Base layout.
 */
const OrdersNewPageBaseLayout = ({
	children,
}: {
	children: ReactNode | string;
}) => {
	return (
		<Flex direction="column" h="100vh" w="100vw">
			{/* HEADER */}
			<OrdersNewPageHeader />
			{/* BODY */}
			<Flex flexGrow={1} justifyContent="center" py={6} px={4}>
				{children}
			</Flex>
		</Flex>
	);
};

/**
 * Base layout buttons. Go back and continue.
 */
const OrdersNewPageBaseLayoutButton = ({
	validateLabel = "Continuer",
	validateTooltip,
	onValidate,
}: {
	validateLabel?: string;
	validateTooltip?: (isValid: boolean) => string | undefined;
	onValidate?: () => void;
}) => {
	const { goPreviousStep, goNextStep, canGoNextStep } = useNewOrderStore();

	const handleContinue = async () => {
		if (onValidate) {
			return onValidate();
		}
		if (canGoNextStep()) {
			goNextStep();
		}
	};
	const tooltipContent = validateTooltip
		? validateTooltip(canGoNextStep())
		: undefined;
	return (
		<Flex gap="4">
			<Button variant="outline" w="fit-content" onClick={goPreviousStep}>
				Retour
			</Button>
			{tooltipContent == null ? (
				<Button
					w="fit-content"
					bgColor="brand.700"
					color="white"
					isDisabled={!canGoNextStep()}
					onClick={handleContinue}
				>
					{validateLabel}
				</Button>
			) : (
				<Tooltip content={tooltipContent}>
					<Button
						w="fit-content"
						bgColor="brand.700"
						color="white"
						isDisabled={!canGoNextStep()}
						onClick={handleContinue}
					>
						{validateLabel}
					</Button>
				</Tooltip>
			)}
		</Flex>
	);
};

/**
 * Display audio badge.
 */
const SelectUserCircleBadge = ({
	value,
	role = false,
	userSelected,
	setUserSelected,
}: {
	value: Partial<User>;
	role?: boolean;
	userSelected?: User;
	setUserSelected: (user?: User) => void;
}) => {
	return (
		<Flex
			position="relative"
			direction="column"
			alignItems="center"
			gap={4}
			onClick={() => {
				setUserSelected(value as User);
			}}
		>
			<Tooltip content={`${value.firstName} ${value.lastName}`} w={"10rem"}>
				<Avatar
					name={`${value.firstName} ${value.lastName}`}
					_hover={{ transform: "scale(1.05)" }}
					transition="all 100ms east-out"
					src="https://bit.ly/cbouchard"
					h="96px"
					w="96px"
					outline={userSelected === value ? "solid 2px #1B58F5" : undefined}
				/>
			</Tooltip>
			{userSelected === value && (
				<CheckCircleIcon
					color="brand.600"
					position="absolute"
					right="4px"
					top="6px"
					bgColor="white"
					borderRadius="xl"
				/>
			)}
			<Text
				textTransform="capitalize"
				lineHeight="21px"
				fontSize="14px"
				fontWeight="500"
				color="gray.700"
			>
				{value.firstName}
			</Text>
			{role && (
				<Text
					textTransform="capitalize"
					lineHeight="18px"
					fontSize="12px"
					fontWeight="400"
					color="gray.500"
				>
					{i18n.t(value.role ?? "")}
				</Text>
			)}
		</Flex>
	);
};

/**
 * New audio invitation modal.
 */
const OrdersNewPageChooseAudioNewAudioModal = ({
	isOpen,
	onClose,
}: {
	isOpen: boolean;
	onClose: () => void;
}) => {
	const { getCenter } = useNewOrderStore();
	const newAudioFormRef = useRef<InviteUserFormHandler>(null);
	const { mutate: mutateSendInvitations } = useMutation(sendInvitations);
	const handleNewAudio = () => {
		const center = getCenter();
		if (newAudioFormRef.current && center) {
			mutateSendInvitations({
				center,
				users: newAudioFormRef.current.values(),
			});
			onClose();
		}
	};

	return (
		<SimpleModal
			icon={<AddIcon color="brand.700" />}
			title="Ajouter un audioprothésiste"
			isOpen={isOpen}
			onClose={onClose}
			w="min(60rem, 90vw)"
			onClick={handleNewAudio}
		>
			<InviteUserForm
				ref={newAudioFormRef}
				role={UserCenterRole.AUDIOPROSTHESIST}
			></InviteUserForm>
		</SimpleModal>
	);
};

const SelectUserCircle = ({
	users,
	w = "100%",
	role = false,
	userSelected,
	setUserSelected,
}: {
	users: Partial<User>[];
	w?: string;
	role?: boolean;
	userSelected?: User;
	setUserSelected: (user?: User) => void;
}) => {
	const newAudioModal = useDisclosure();

	return (
		<Grid
			templateColumns="1fr 1fr 1fr 1fr 1fr"
			w={w}
			justifyItems="center"
			gap="20px 4px"
		>
			{users &&
				users.map((user) => (
					<SelectUserCircleBadge
						key={user.id}
						value={user}
						role={role}
						userSelected={userSelected}
						setUserSelected={setUserSelected}
					/>
				))}

			<OrdersNewPageChooseAudioNewAudioModal
				isOpen={newAudioModal.isOpen}
				onClose={newAudioModal.onClose}
			/>
		</Grid>
	);
};

/**
 * Layout for audio responsible selection.
 */
const OrdersNewPageChooseAudioResponsibleLayout = () => {
	const { center, setCurrentCenter } = useCenters();
	const { getCenter: getOrderCenter, setCenter: setOrderCenter } =
		useNewOrderStore();
	const { audioResponsible, setAudioResponsible, setStep } = useNewOrderStore();
	const { data: audios } = useCenterAudio(center?.id);

	useEffect(() => {
		/**
		 * If first visit on the page, orderCenter should be null.
		 * In that case load the currently selected center as the default center.
		 */
		if (center && !getOrderCenter()) {
			setOrderCenter(center);
		}
	}, []);
	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>Qui est l'audioprothésiste responsable ?</PageTitle>

				<CenterSelect
					centerSelected={center ?? undefined}
					setCenterSelected={(center?: Center) => {
						setCurrentCenter(center ?? null);
						setOrderCenter(center);
					}}
				/>

				<SelectUserCircle
					users={audios?.data ?? []}
					w="min(44rem, 90vw)"
					userSelected={audioResponsible}
					setUserSelected={(user?: User) => {
						setAudioResponsible(user);
						setStep(NewOrderStep.CHOOSE_PATIENT);
					}}
				/>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

/**
 * Layout for patient selection.
 */
const OrdersNewPageChoosePatientLayout = () => {
	const { getCenter, setPatient, patient } = useNewOrderStore();
	const { externalNumberId, setExternalNumberId } = useNewOrderStore();
	const newPatientModal = useDisclosure();
	const newPatientFormRef = useRef<PatientFormHandler>(null);
	const queryClient = useQueryClient();
	const { mutate: mutateCreatePatient } = useMutation(createPatient, {
		onSuccess: (data: Patient) => {
			setPatient(data);
			newPatientModal.onClose();
			queryClient.invalidateQueries("center-patients");
		},
	});

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>Qui est le patient ?</PageTitle>

				<Flex direction="column" gap={4} w="min(40rem,90vw)">
					<PatientSelect
						center={getCenter()}
						patientSelected={patient}
						setPatientSelected={(patient?: Patient) => {
							setPatient(patient);
						}}
						onNewPatient={() => newPatientModal.onOpen()}
					/>
					<Input
						placeholder="Numéro de commande externe (facultatif)"
						name={""}
						tooltip={"Numéro de commande en externe"}
						defaultValue={externalNumberId}
						onChange={(evt) => setExternalNumberId(evt.target.value)}
					></Input>
				</Flex>

				<OrdersNewPageBaseLayoutButton />

				<SimpleModal
					icon={<AddUserIcon w="32px" h="32px" />}
					title="Ajouter un patient"
					isOpen={newPatientModal.isOpen}
					onClose={newPatientModal.onClose}
					onClick={async () => {
						const center = getCenter();
						if (newPatientFormRef.current && center) {
							const valid = await newPatientFormRef.current.valid();
							if (valid) {
								const patient = newPatientFormRef.current.values();
								mutateCreatePatient({
									patient: {
										...patient,
										centerId: center.id,
									},
								});
							}
						}
					}}
				>
					<PatientForm ref={newPatientFormRef} />
				</SimpleModal>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const CardSelectable = ({
	children,
	title,
	subtitle,
	onClick,
	activated = false,
	inline = false,
	w = "100%",
}: {
	children?: ReactNode;
	title?: string;
	subtitle?: string;
	onClick?: () => void;
	activated?: boolean;
	inline?: boolean;
	w?: string;
}) => {
	const style = activated
		? { border: "solid 2px", borderColor: "brand.700", bgColor: "brand.50" }
		: { border: "solid 1px", borderColor: "gray.200", bgColor: "white" };
	return (
		<Flex
			direction={inline ? "row" : "column"}
			justify={inline ? "space-between" : undefined}
			gap={2}
			w={w}
			p={4}
			borderRadius="md"
			{...style}
			onClick={onClick}
			cursor="pointer"
		>
			<Box>
				{title !== undefined && (
					<Text
						lineHeight="27px"
						fontSize="18px"
						fontWeight="600"
						color="gray.700"
					>
						{title}
					</Text>
				)}
				{title === undefined && children}
				{subtitle && (
					<Text
						lineHeight="24px"
						fontSize="16px"
						fontWeight="400"
						color="gray.600"
					>
						{subtitle}
					</Text>
				)}
			</Box>
			{inline && <Radio isChecked={activated} />}
		</Flex>
	);
};

const OrdersNewPageChooseCommandTypeLayout = () => {
	const { type, setType } = useNewOrderStore();
	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>De quel type de commande s'agit-il&nbsp;?</PageTitle>

				<Flex direction="column" gap={4} w="40rem">
					<CardSelectable
						title="Nouvelle commande"
						subtitle="Vous souhaitez utiliser de nouvelles empreintes"
						activated={type === NewOrderType.NEW}
						onClick={() => setType(NewOrderType.NEW)}
					/>
					<CardSelectable
						title="Refonte"
						subtitle="Vous souhaitez utiliser d'anciennes empreintes"
						activated={type === NewOrderType.REWORK}
						onClick={() => setType(NewOrderType.REWORK)}
					/>
					<Flex
						w="100%"
						border="solid 1px"
						borderColor="orange.300"
						borderRadius="xl"
						bgColor="orange.50"
						p={4}
						visibility={type === NewOrderType.REWORK ? "visible" : "hidden"}
					>
						<Box lineHeight="21px" fontSize="16px" fontWeight="600">
							Attention, si l'empreinte a été réalisée il y a plus de quatres
							ans, nous vous invitons à la renouveler.
						</Box>
					</Flex>
				</Flex>

				<OrdersNewPageBaseLayoutButton />
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const OrdersNewPageChooseEarprintShippingTypeLayout = () => {
	const { earprintShippingType, setEarprintShippingType } = useNewOrderStore();
	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>Sélectionner le mode d'envoi d'empreintes</PageTitle>

				<Flex direction="column" gap={4}>
					<CardSelectable
						w="40rem"
						title="Empreintes Natus/Otoscan"
						subtitle="+1,50€/embout"
						inline
						activated={
							earprintShippingType ===
							NewOrderEarprintShippingType.NATUS_OTOSCAN
						}
						onClick={() =>
							setEarprintShippingType(
								NewOrderEarprintShippingType.NATUS_OTOSCAN,
							)
						}
					/>
					<CardSelectable
						w="40rem"
						title="Empreintes numériques"
						inline
						activated={
							earprintShippingType === NewOrderEarprintShippingType.DIGITAL
						}
						onClick={() =>
							setEarprintShippingType(NewOrderEarprintShippingType.DIGITAL)
						}
					/>
					<CardSelectable
						w="40rem"
						title="Empreintes physiques"
						inline
						activated={
							earprintShippingType === NewOrderEarprintShippingType.PHYSICAL
						}
						onClick={() =>
							setEarprintShippingType(NewOrderEarprintShippingType.PHYSICAL)
						}
					/>
				</Flex>

				<OrdersNewPageBaseLayoutButton />
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const RightEar = () => {
	return (
		<svg
			width="68"
			height="104"
			viewBox="0 0 68 104"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<g clipPath="url(#clip0_4979_20904)">
				<path
					d="M0.798828 42.6495C1.15896 32.2984 3.58211 21.2373 10.3474 11.5035C14.5969 5.39162 20.3795 1.49194 27.8393 0.36011C33.7455 -0.535067 39.5024 0.252071 44.9557 2.57233C49.0664 4.32153 51.6541 7.80448 53.8766 11.5447C56.2638 15.5678 58.0027 19.9305 60.3384 23.9794C61.4548 25.919 62.6535 27.7968 64.6599 28.98C65.2516 29.3299 65.5191 29.8958 65.159 30.5132C64.7474 31.2128 64.0992 31.0997 63.4664 30.7704C62.1853 30.0913 61.2233 29.0624 60.3384 27.9511C58.939 26.1968 57.9204 24.2161 56.912 22.2251C55.384 19.2051 53.9384 16.144 52.3024 13.1755C49.1898 7.52152 44.8014 3.82249 38.1853 2.79355C31.3326 1.7286 24.8554 2.17619 18.9287 6.07587C14.1956 9.18841 11.0368 13.6386 8.66507 18.6752C4.66764 27.1537 2.82069 36.126 3.05735 45.4739C3.24256 52.6919 4.6059 59.6784 7.89851 66.2019C9.5088 69.3916 11.9885 71.9022 14.2625 74.5723C20.0451 81.3582 25.8123 88.1595 32.3255 94.2765C33.9512 95.8045 35.6644 97.2296 37.4805 98.5312C41.5705 101.459 46.2265 102.066 51.0728 101.721C53.7429 101.531 56.3204 100.913 58.6869 99.591C62.3602 97.5382 64.377 94.4206 64.8914 90.2431C65.1898 87.8148 64.8966 85.4636 64.4439 83.1074C64.377 82.773 64.3255 82.4334 64.2484 82.099C64.0786 81.3736 64.2484 80.7614 65.0201 80.5865C65.8175 80.4064 66.1467 80.9775 66.3474 81.6566C66.867 83.4366 67.1191 85.2682 67.1911 87.1048C67.4072 92.7022 65.7969 97.5074 60.9249 100.795C58.6046 102.359 56.022 103.218 53.2901 103.681C50.3937 104.17 47.4972 104.077 44.5905 103.686C39.2863 102.976 35.3043 99.9254 31.5229 96.4939C24.8657 90.4591 19.1242 83.5755 13.2644 76.8C12.2818 75.663 11.3352 74.5003 10.3268 73.3942C5.07407 67.6579 2.82584 60.6251 1.58082 53.1653C1.0715 50.1196 0.824552 47.0482 0.809118 42.6649L0.798828 42.6495Z"
					fill="#B91C1C"
				/>
				<path
					d="M51.3709 29.16C51.3606 32.7613 49.7246 35.6732 47.394 38.2455C44.3792 41.5741 40.6956 44.1053 37.0275 46.6417C36.441 47.0481 35.7825 47.1356 35.3297 46.4616C34.8616 45.7671 35.2834 45.2475 35.8493 44.841C38.2159 43.1381 40.6185 41.4918 42.8255 39.578C44.5439 38.0912 46.1696 36.5118 47.4352 34.5928C49.2204 31.8661 49.6731 28.9594 48.639 25.8828C47.641 22.9143 46.3136 20.0848 44.6725 17.3992C44.086 16.4372 43.4172 16.1748 42.3317 16.432C37.331 17.605 33.349 20.3368 30.1336 24.2725C29.9998 24.4372 29.8815 24.6172 29.7426 24.7819C29.2899 25.3221 28.7394 25.677 28.0808 25.1883C27.4275 24.6995 27.5818 24.077 27.9985 23.4854C29.1664 21.8237 30.5812 20.3883 32.1503 19.1175C34.7741 16.9928 37.6757 15.3722 40.9683 14.5079C41.241 14.4359 41.6423 14.4719 41.74 14.1426C41.8583 13.7362 41.4468 13.5613 41.1998 13.3555C37.6603 10.4384 33.6526 10.2584 29.4185 11.3542C20.2043 13.7413 14.8744 20.0796 11.803 28.6918C10.1516 33.3272 9.32329 38.1066 9.11236 43.0249C8.46413 58.1349 15.7027 68.7844 27.9882 76.5992C29.2899 77.4275 30.7715 77.9214 32.2172 78.4565C32.9169 78.7137 33.7091 79.0121 33.3902 79.969C33.0506 80.9876 32.212 80.7664 31.4506 80.504C26.3625 78.7548 22.0204 75.8275 18.3317 71.9484C12.9452 66.2893 8.63905 59.8687 7.44033 52.059C5.63455 40.3343 7.53294 29.1343 14.2879 19.1484C18.7792 12.5118 25.4108 9.21916 33.2718 8.5452C37.4596 8.18507 41.1329 9.82109 43.9625 12.9439C47.6101 16.9568 49.8017 21.7722 51.1754 26.9786C51.3657 27.6938 51.3709 28.4192 51.376 29.1497L51.3709 29.16Z"
					fill="#B91C1C"
				/>
				<path
					d="M51.911 50.5513C51.9573 51.7345 51.6074 52.8406 51.2525 53.9416C50.7174 55.6136 50.1412 57.2702 49.673 58.968C49.1483 60.8921 49.5753 62.5333 50.8306 63.9841C51.7824 65.085 52.801 66.1294 53.8094 67.1789C54.5554 67.9506 55.2756 68.7429 55.8364 69.6638C56.675 71.0426 56.7521 72.4162 55.7746 73.7487C54.7714 75.1172 53.4184 75.6934 51.7309 75.3744C50.6197 75.1635 49.6113 74.685 48.6595 74.1037C47.5586 73.4349 46.4319 72.8021 45.4081 72.0355C42.8975 70.1525 40.1553 69.7101 37.1354 70.4098C35.8904 70.6979 34.6145 70.8265 33.3129 70.739C28.2299 70.3995 24.6955 65.6201 23.718 61.566C22.2672 55.5365 23.5637 49.9185 26.4499 44.5886C29.7425 38.5024 34.6608 34.0008 40.5155 30.4509C41.2049 30.0342 42.0023 29.4117 42.6299 30.4098C43.2576 31.4078 42.3624 31.876 41.673 32.2927C37.5161 34.7931 33.8428 37.8593 30.9103 41.7332C27.237 46.5847 25.0042 51.9506 25.3746 58.1808C25.5599 61.2882 26.6505 64.0098 28.801 66.2889C30.9978 68.6194 33.7656 68.9281 36.6364 68.2696C40.356 67.4156 43.664 68.0587 46.7354 70.276C47.7746 71.0271 48.9013 71.6651 49.992 72.339C50.6505 72.7455 51.3502 73.0747 52.1168 73.2188C52.9142 73.368 53.5418 73.0593 54.0203 72.4214C54.4988 71.7783 54.2158 71.1815 53.8505 70.6413C52.8576 69.1751 51.4531 68.0792 50.3316 66.7262C49.7296 66.0008 49.0865 65.3062 48.5361 64.5448C47.3168 62.8574 46.9412 61.0053 47.4299 58.9423C47.9084 56.9358 48.5669 54.9911 49.2306 53.0464C49.5804 52.0175 49.7708 50.9783 49.6782 49.8927C49.5187 47.9892 48.6698 47.3461 46.8486 47.8863C43.4994 48.8792 40.8653 50.9731 38.5039 53.458C38.2672 53.705 38.0717 54.0033 37.8042 54.2091C37.3412 54.5641 36.837 54.5641 36.3997 54.1577C36.0087 53.7924 35.9007 53.2779 36.2454 52.8767C39.3888 49.1776 42.9592 46.0702 48.0267 45.5352C50.0898 45.314 51.4994 46.8882 51.839 49.2702C51.9007 49.6921 51.8853 50.1242 51.9058 50.5513H51.911Z"
					fill="#B91C1C"
				/>
			</g>
			<defs>
				<clipPath id="clip0_4979_20904">
					<rect
						width="66.4026"
						height="104"
						fill="white"
						transform="translate(0.798828)"
					/>
				</clipPath>
			</defs>
		</svg>
	);
};

const LeftEar = () => {
	return (
		<svg
			width="68"
			height="104"
			viewBox="0 0 68 104"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<g clipPath="url(#clip0_4979_20910)">
				<path
					d="M67.2012 42.6495C66.841 32.2984 64.4179 21.2373 57.6526 11.5035C53.4031 5.39162 47.6205 1.49194 40.1607 0.36011C34.2545 -0.535067 28.4976 0.252071 23.0443 2.57233C18.9336 4.32153 16.3459 7.80448 14.1234 11.5447C11.7362 15.5678 9.99731 19.9305 7.66161 23.9794C6.54522 25.919 5.3465 27.7968 3.34007 28.98C2.74844 29.3299 2.48091 29.8958 2.84103 30.5132C3.25261 31.2128 3.90084 31.0997 4.53364 30.7704C5.81467 30.0913 6.77673 29.0624 7.66161 27.9511C9.06097 26.1968 10.0796 24.2161 11.088 22.2251C12.616 19.2051 14.0616 16.144 15.6976 13.1755C18.8102 7.52152 23.1986 3.82249 29.8147 2.79355C36.6674 1.7286 43.1446 2.17619 49.0713 6.07587C53.8044 9.18841 56.9632 13.6386 59.3349 18.6752C63.3324 27.1537 65.1793 36.126 64.9427 45.4739C64.7574 52.6919 63.3941 59.6784 60.1015 66.2019C58.4912 69.3916 56.0115 71.9022 53.7375 74.5723C47.9549 81.3582 42.1877 88.1595 35.6745 94.2765C34.0488 95.8045 32.3356 97.2296 30.5195 98.5312C26.4295 101.459 21.7735 102.066 16.9272 101.721C14.2571 101.531 11.6796 100.913 9.31306 99.591C5.63975 97.5382 3.62303 94.4206 3.10856 90.2431C2.81017 87.8148 3.10342 85.4636 3.55615 83.1074C3.62303 82.773 3.67448 82.4334 3.75165 82.099C3.92142 81.3736 3.75165 80.7614 2.97994 80.5865C2.18252 80.4064 1.85326 80.9775 1.65261 81.6566C1.133 83.4366 0.880905 85.2682 0.808884 87.1048C0.592804 92.7022 2.20309 97.5074 7.07512 100.795C9.39538 102.359 11.978 103.218 14.7099 103.681C17.6063 104.17 20.5028 104.077 23.4095 103.686C28.7137 102.976 32.6957 99.9254 36.4771 96.4939C43.1343 90.4591 48.8758 83.5755 54.7356 76.8C55.7182 75.663 56.6648 74.5003 57.6732 73.3942C62.9259 67.6579 65.1742 60.6251 66.4192 53.1653C66.9285 50.1196 67.1754 47.0482 67.1909 42.6649L67.2012 42.6495Z"
					fill="#2563EB"
				/>
				<path
					d="M16.6291 29.16C16.6394 32.7613 18.2754 35.6732 20.606 38.2455C23.6208 41.5741 27.3044 44.1053 30.9725 46.6417C31.559 47.0481 32.2175 47.1356 32.6703 46.4616C33.1384 45.7671 32.7166 45.2475 32.1507 44.841C29.7841 43.1381 27.3815 41.4918 25.1745 39.578C23.4561 38.0912 21.8304 36.5118 20.5648 34.5928C18.7796 31.8661 18.3269 28.9594 19.361 25.8828C20.359 22.9143 21.6864 20.0848 23.3275 17.3992C23.914 16.4372 24.5828 16.1748 25.6683 16.432C30.669 17.605 34.651 20.3368 37.8664 24.2725C38.0002 24.4372 38.1185 24.6172 38.2574 24.7819C38.7101 25.3221 39.2606 25.677 39.9192 25.1883C40.5725 24.6995 40.4182 24.077 40.0015 23.4854C38.8336 21.8237 37.4188 20.3883 35.8497 19.1175C33.2259 16.9928 30.3243 15.3722 27.0317 14.5079C26.759 14.4359 26.3577 14.4719 26.26 14.1426C26.1417 13.7362 26.5532 13.5613 26.8002 13.3555C30.3397 10.4384 34.3474 10.2584 38.5815 11.3542C47.7957 13.7413 53.1256 20.0796 56.197 28.6918C57.8484 33.3272 58.6767 38.1066 58.8876 43.0249C59.5359 58.1349 52.2973 68.7844 40.0118 76.5992C38.7101 77.4275 37.2285 77.9214 35.7828 78.4565C35.0831 78.7137 34.2909 79.0121 34.6098 79.969C34.9494 80.9876 35.788 80.7664 36.5494 80.504C41.6375 78.7548 45.9796 75.8275 49.6683 71.9484C55.0548 66.2893 59.361 59.8687 60.5597 52.059C62.3655 40.3343 60.4671 29.1343 53.7121 19.1484C49.2208 12.5118 42.5892 9.21916 34.7282 8.5452C30.5404 8.18507 26.8671 9.82109 24.0375 12.9439C20.3899 16.9568 18.1983 21.7722 16.8246 26.9786C16.6343 27.6938 16.6291 28.4192 16.624 29.1497L16.6291 29.16Z"
					fill="#2563EB"
				/>
				<path
					d="M16.089 50.5513C16.0427 51.7345 16.3926 52.8406 16.7475 53.9416C17.2826 55.6136 17.8588 57.2702 18.327 58.968C18.8517 60.8921 18.4247 62.5333 17.1694 63.9841C16.2176 65.085 15.199 66.1294 14.1906 67.1789C13.4446 67.9506 12.7244 68.7429 12.1636 69.6638C11.325 71.0426 11.2479 72.4162 12.2254 73.7487C13.2286 75.1172 14.5816 75.6934 16.2691 75.3744C17.3803 75.1635 18.3887 74.685 19.3405 74.1037C20.4414 73.4349 21.5681 72.8021 22.5919 72.0355C25.1025 70.1525 27.8447 69.7101 30.8646 70.4098C32.1096 70.6979 33.3855 70.8265 34.6871 70.739C39.7701 70.3995 43.3045 65.6201 44.282 61.566C45.7328 55.5365 44.4363 49.9185 41.5501 44.5886C38.2575 38.5024 33.3392 34.0008 27.4845 30.4509C26.7951 30.0342 25.9977 29.4117 25.3701 30.4098C24.7424 31.4078 25.6376 31.876 26.327 32.2927C30.4839 34.7931 34.1572 37.8593 37.0897 41.7332C40.763 46.5847 42.9958 51.9506 42.6254 58.1808C42.4401 61.2882 41.3495 64.0098 39.199 66.2889C37.0022 68.6194 34.2344 68.9281 31.3636 68.2696C27.644 67.4156 24.336 68.0587 21.2646 70.276C20.2254 71.0271 19.0987 71.6651 18.008 72.339C17.3495 72.7455 16.6498 73.0747 15.8832 73.2188C15.0858 73.368 14.4582 73.0593 13.9797 72.4214C13.5012 71.7783 13.7842 71.1815 14.1495 70.6413C15.1424 69.1751 16.5469 68.0792 17.6684 66.7262C18.2704 66.0008 18.9135 65.3062 19.4639 64.5448C20.6832 62.8574 21.0588 61.0053 20.5701 58.9423C20.0916 56.9358 19.4331 54.9911 18.7694 53.0464C18.4196 52.0175 18.2292 50.9783 18.3218 49.8927C18.4813 47.9892 19.3302 47.3461 21.1514 47.8863C24.5006 48.8792 27.1347 50.9731 29.4961 53.458C29.7328 53.705 29.9283 54.0033 30.1958 54.2091C30.6588 54.5641 31.163 54.5641 31.6003 54.1577C31.9913 53.7924 32.0993 53.2779 31.7546 52.8767C28.6112 49.1776 25.0408 46.0702 19.9733 45.5352C17.9102 45.314 16.5006 46.8882 16.161 49.2702C16.0993 49.6921 16.1147 50.1242 16.0942 50.5513H16.089Z"
					fill="#2563EB"
				/>
			</g>
			<defs>
				<clipPath id="clip0_4979_20910">
					<rect
						width="66.4026"
						height="104"
						fill="white"
						transform="matrix(-1 0 0 1 67.2012 0)"
					/>
				</clipPath>
			</defs>
		</svg>
	);
};

type EarSectionFormSectionOptions = {
	key: string;
	disabled: boolean;
} & TSelectDropdownOption;

/**
 * Display form section. Either as a list of checkpoints are a a dropdown selection list.
 */
const EarSectionFormSection = ({
	title,
	description,
	options,
	value,
	type,
	setValue,
}: {
	title: string;
	description?: string;
	options?: EarSectionFormSectionOptions[];
	value?: TSelectDropdownOption;
	type?: "dropdown" | "radio";
	setValue?: (value: TSelectDropdownOption) => void;
}) => {
	const handleSetValue = (option: EarSectionFormSectionOptions) => {
		if (option.disabled === false) {
			if (setValue) {
				setValue(option);
			}
		}
	};
	const DropdownVariant = () => {
		return (
			<SelectDropdown
				value={value}
				disabled={
					Array.isArray(options) && _.every(options.map((opt) => opt.disabled))
				}
			>
				{Array.isArray(options) &&
					options
						.sort((a, b) => (a.disabled ? 1 : 0) - (b.disabled ? 1 : 0))
						.map((option) => {
							return (
								<SelectDropdown.Option
									key={option.key}
									onClick={() => handleSetValue(option)}
									disabled={option.disabled}
								>
									{option.label}
								</SelectDropdown.Option>
							);
						})}
			</SelectDropdown>
		);
	};
	const RadioVariant = () => {
		return (
			<Grid gridTemplateColumns={"1fr 1fr"}>
				{Array.isArray(options) &&
					options.map((option) => {
						return (
							<Box onClick={() => handleSetValue(option)} key={option.key}>
								<Radio
									key={option.key}
									isDisabled={option.disabled}
									isChecked={option.value === value?.value}
								>
									{option.label}
								</Radio>
							</Box>
						);
					})}
			</Grid>
		);
	};

	let defaultVariant = DropdownVariant;
	if (Array.isArray(options) && options.length < 6) {
		defaultVariant = RadioVariant;
	}

	return (
		<Box>
			<Flex alignItems="center" pb={2} gap={2}>
				<Text lineHeight="21px" fontSize="16px" fontWeight="600">
					{title}
				</Text>
				{description && (
					<Tooltip content={description}>
						<CircleHelpIcon />
					</Tooltip>
				)}
			</Flex>
			{type === undefined && defaultVariant()}
			{type === "dropdown" && <DropdownVariant />}
			{type === "radio" && <RadioVariant />}
		</Box>
	);
};

const EarSectionForm = ({ side }: { side: NewOrderEarSide }) => {
	const { earmold } = useNewOrderStore();
	const { getVariantValue, setVariantValue } = useNewOrderStore();
	const { isDisabled, getForm } = useNewOrderStore();
	const { data: variants } = useEarmoldVariants(earmold);

	if (!Array.isArray(variants)) {
		return <></>;
	}
	return (
		<>
			{variants.map((variant) => (
				<Box key={variant.uuid}>
					<EarSectionSpacer />
					<EarSectionFormSection
						title={variant.label}
						description={
							variant.description?.trim() == ""
								? undefined
								: variant.description?.trim()
						}
						options={variant.options.data.map((option) => ({
							key: option.uuid,
							label: option.label,
							value: option.uuid,
							disabled: isDisabled(variant, option, getForm(side)),
						}))}
						value={{
							label: getVariantValue(side, variant.id.toString())?.label ?? "",
							value: getVariantValue(side, variant.id.toString())?.uuid ?? "",
						}}
						setValue={(option: TSelectDropdownOption) => {
							const value = variant.options.data.find(
								(v) => v.uuid === option.value,
							);
							if (value) {
								setVariantValue(side, variant.id.toString(), value);
							}
						}}
					/>
				</Box>
			))}
		</>
	);
};

const EarSectionType = () => {
	const { setEarmold, earmold } = useNewOrderStore();
	// NOTE: No pagination required here (at least at short term) so use an arbitrary high value (100_000)
	const earmolds = useEarmoldsPaginated(1, 100_000, undefined, {
		status: "active",
	});

	if (earmolds == undefined) {
		return <></>;
	}
	return (
		<EarSectionFormSection
			title="Type d'embout"
			type="radio"
			options={
				earmolds.data.map((e) => ({
					key: e.id.toString(),
					value: e.id.toString(),
					label: e.label,
					disabled: false,
				})) ?? undefined
			}
			value={{
				label: earmold ?? "",
				value: earmold ?? "",
			}}
			setValue={async (opt) => {
				const variants = await fetchEarmoldVariants(opt.value);
				setEarmold(opt.value, variants ?? undefined);
			}}
		/>
	);
};

const EarSectionSpacer = (props: BoxProps) => {
	return (
		<Box
			w="100%"
			borderBottom="solid 1px"
			borderColor="gray.200"
			py={props.py}
			my={props.my}
			position="relative"
		>
			{props.content && (
				<Text
					position="absolute"
					top="-13px"
					left="50%"
					bgColor={props.bgColor}
					px={2}
				>
					{props.content}
				</Text>
			)}
		</Box>
	);
};

const EarSectionRight = () => {
	const { enableEar, hasRightEar, earprintShippingType, setFile, rightFile } =
		useNewOrderStore();
	const { reworkCommand, earmold, isRework } = useNewOrderStore();
	const ref = useRef<HTMLInputElement>(null);
	const toast = useSimpleToast();
	const { mutate: mutateCreateFile } = useMutation(createFile, {
		onSuccess: (data: SavedFile) => {
			setFile(NewOrderEarSide.RIGHT, data);
		},
		onError: () => {
			toast.error(
				"Fichier",
				"Erreur d'upload de fichier la limite est de 10MB",
			);
		},
	});

	return (
		<Flex
			direction="column"
			justifyContent="center"
			border="solid 1px"
			borderColor="gray.200"
			borderRadius="xl"
			p={4}
			bgColor={hasRightEar ? "#FEF2F2" : undefined}
		>
			<Flex
				direction="column"
				gap={4}
				alignItems="center"
				onClick={() => {
					if (reworkCommand == undefined) {
						enableEar(NewOrderEarSide.RIGHT, !hasRightEar);
					} else {
						const reworkCommandDraft = reworkCommand.draft;
						if (reworkCommandDraft?.hasRightEar === true) {
							enableEar(NewOrderEarSide.RIGHT, !hasRightEar);
						}
					}
				}}
			>
				<RightEar />
				Oreille droite
			</Flex>

			{hasRightEar && (
				<Flex direction="column" gap={8} pt={8}>
					<>
						{earprintShippingType === NewOrderEarprintShippingType.DIGITAL &&
							isRework() === false && (
								<Flex direction="column">
									<Text mb={2}>Empreinte</Text>
									<FileInlineInput
										ref={ref}
										name={"rightFile"}
										accept=".stl"
										fileName={rightFile?.fileName}
										files={
											rightFile
												? [
														{
															id: rightFile.id,
															name: rightFile.fileName,
														},
													]
												: undefined
										}
										onFileChange={(files: File[] | null) => {
											if (files && files.length > 0) {
												const file = files[0];
												const fileName = file.name;
												mutateCreateFile({ file, fileName });
											}
										}}
									/>
								</Flex>
							)}
					</>
					<EarSectionSpacer />
					<EarSectionType />
					{earmold && (
						<>
							<EarSectionForm side={NewOrderEarSide.RIGHT} />
						</>
					)}
				</Flex>
			)}
		</Flex>
	);
};

const EarSectionPreviousCommandRight = () => {
	const { hasRightEar, enableEar } = useNewOrderStore();
	const { patient } = useNewOrderStore();
	const { reworkCommand, reworkCommandOld, setReworkCommand } =
		useNewOrderStore();
	const commands = usePatientCommands(patient?.id)(
		1,
		5,
		undefined,
		{ not_status: CommandStatus.DRAFT },
		["-createdAt"],
	);

	return (
		<Flex
			direction="column"
			justifyContent="center"
			border="solid 1px"
			borderColor="gray.200"
			borderRadius="xl"
			p={4}
			bgColor={hasRightEar ? "#FEF2F2" : undefined}
		>
			<Flex
				direction="column"
				gap={4}
				alignItems="center"
				onClick={() => {
					if (reworkCommand == undefined) {
						enableEar(NewOrderEarSide.RIGHT, !hasRightEar);
					} else {
						const reworkCommandDraft = reworkCommand.draft;
						if (reworkCommandDraft?.hasRightEar === true) {
							enableEar(NewOrderEarSide.RIGHT, !hasRightEar);
						}
					}
				}}
			>
				<RightEar />
				Oreille droite
			</Flex>

			<EarSectionSpacer py={2} />
			{hasRightEar && (
				<Flex direction="column" gap={8} pt={8} alignItems={"center"}>
					<Text lineHeight="36px" fontSize="24px" fontWeight="600">
						Sélectionner la commande concernée
					</Text>

					{commands &&
						Array.isArray(commands.data) &&
						commands.data.map((command) => (
							<CardSelectable
								key={command.id}
								onClick={() => setReworkCommand(command)}
								activated={command.id === reworkCommand?.id}
								inline
							>
								<Flex
									direction="row"
									gap={4}
									justifyContent={"center"}
									alignItems={"center"}
								>
									<Text
										lineHeight="18px"
										fontSize="12px"
										fontWeight="400"
										color="gray.500"
									>
										Commande
									</Text>
									<Text
										lineHeight="27px"
										fontSize="18px"
										fontWeight="600"
										color="gray.700"
									>
										{command.exposedId}
									</Text>
									<Text lineHeight="21px" fontSize="14px" fontWeight="400">
										{format(command.createdAt, "dd/MM/yyyy")}
									</Text>
								</Flex>
							</CardSelectable>
						))}

					<EarSectionSpacer content="ou" bgColor="#FEF2F2" />

					<Text
						w="33rem"
						textAlign="center"
						lineHeight="36px"
						fontSize="24px"
						fontWeight="600"
					>
						Si commande antérieure alors entrez le numéro de la commande
						concernée
					</Text>
					<Text
						w="33rem"
						textAlign="center"
						lineHeight="24px"
						fontSize="16px"
						fontWeight="400"
					>
						Si la commande est indisponible dans la liste alors veuillez rentrer
						le numéro de la commande concernée. ex: 2022-15608.
					</Text>

					<Input
						name="orderId"
						placeholder="Numéro de la commande"
						value={reworkCommandOld}
						setValue={setReworkCommand}
					/>
				</Flex>
			)}
		</Flex>
	);
};

const EarSectionPreviousCommandLeft = () => {
	const { hasLeftEar, enableEar } = useNewOrderStore();
	const { patient } = useNewOrderStore();
	const { reworkCommand, reworkCommandOld, setReworkCommand } =
		useNewOrderStore();
	const commands = usePatientCommands(patient?.id)(
		1,
		5,
		undefined,
		{ not_status: CommandStatus.DRAFT },
		["-createdAt"],
	);

	return (
		<Flex
			direction="column"
			justifyContent="center"
			border="solid 1px"
			borderColor="gray.200"
			borderRadius="xl"
			p={4}
			bgColor={hasLeftEar ? "#EFF6FF" : undefined}
		>
			<Flex
				direction="column"
				gap={4}
				alignItems="center"
				onClick={() => {
					if (reworkCommand == undefined) {
						enableEar(NewOrderEarSide.LEFT, !hasLeftEar);
					} else {
						const reworkCommandDraft = reworkCommand.draft;
						if (reworkCommandDraft?.hasLeftEar === true) {
							enableEar(NewOrderEarSide.LEFT, !hasLeftEar);
						}
					}
				}}
			>
				<LeftEar />
				Oreille gauche
			</Flex>

			<EarSectionSpacer py={2} />
			{hasLeftEar && (
				<Flex direction="column" gap={8} pt={8} alignItems={"center"}>
					<Text lineHeight="36px" fontSize="24px" fontWeight="600">
						Sélectionner la commande concernée
					</Text>

					{commands &&
						Array.isArray(commands.data) &&
						commands.data.map((command) => (
							<CardSelectable
								key={command.id}
								onClick={() => setReworkCommand(command)}
								activated={command.id === reworkCommand?.id}
								inline
							>
								<Flex
									direction="row"
									gap={4}
									justifyContent={"center"}
									alignItems={"center"}
								>
									<Text
										lineHeight="18px"
										fontSize="12px"
										fontWeight="400"
										color="gray.500"
									>
										Commande
									</Text>
									<Text
										lineHeight="27px"
										fontSize="18px"
										fontWeight="600"
										color="gray.700"
									>
										{command.exposedId}
									</Text>
									<Text lineHeight="21px" fontSize="14px" fontWeight="400">
										{format(command.createdAt, "dd/MM/yyyy")}
									</Text>
								</Flex>
							</CardSelectable>
						))}

					<EarSectionSpacer content="ou" bgColor="#EFF6FF" />

					<Text
						w="33rem"
						textAlign="center"
						lineHeight="36px"
						fontSize="24px"
						fontWeight="600"
					>
						Si commande antérieure alors entrez le numéro de la commande
						concernée
					</Text>
					<Text
						w="33rem"
						textAlign="center"
						lineHeight="24px"
						fontSize="16px"
						fontWeight="400"
					>
						Si la commande est indisponible dans la liste alors veuillez rentrer
						le numéro de la commande concernée. ex: 2022-15608.
					</Text>

					<Input
						name="orderId"
						placeholder="Numéro de la commande"
						value={reworkCommandOld}
						setValue={setReworkCommand}
					/>
				</Flex>
			)}
		</Flex>
	);
};

const EarSectionLeft = () => {
	const {
		enableEar,
		hasLeftEar,
		earprintShippingType,
		setFile,
		leftFile,
		isRework,
		reworkCommand,
	} = useNewOrderStore();
	const { earmold } = useNewOrderStore();
	const { mutate: mutateCreateFile } = useMutation(createFile, {
		onSuccess: (data: SavedFile) => {
			setFile(NewOrderEarSide.LEFT, data);
		},
	});

	return (
		<Flex
			direction="column"
			justifyContent="center"
			border="solid 1px"
			borderColor="gray.200"
			borderRadius="xl"
			p={4}
			bgColor={hasLeftEar ? "#EFF6FF" : undefined}
		>
			<Flex
				direction="column"
				gap={4}
				alignItems="center"
				onClick={() => {
					if (reworkCommand == undefined) {
						enableEar(NewOrderEarSide.LEFT, !hasLeftEar);
					} else {
						const reworkCommandDraft = reworkCommand.draft;
						if (reworkCommandDraft?.hasLeftEar === true) {
							enableEar(NewOrderEarSide.LEFT, !hasLeftEar);
						}
					}
				}}
			>
				<LeftEar />
				Oreille gauche
			</Flex>

			{hasLeftEar && (
				<Flex direction="column" gap={8} pt={8}>
					<>
						{earprintShippingType === NewOrderEarprintShippingType.DIGITAL &&
							isRework() === false && (
								<Flex direction="column">
									<Text mb={2}>Empreinte</Text>
									<FileInlineInput
										name={"rightFile"}
										accept=".stl"
										fileName={leftFile?.fileName}
										files={
											leftFile
												? [
														{
															id: leftFile.id,
															name: leftFile.fileName,
														},
													]
												: undefined
										}
										onFileChange={(files: File[] | null) => {
											if (files && files.length > 0) {
												const file = files[0];
												const fileName = file.name;
												mutateCreateFile({ file, fileName });
											}
										}}
									/>
								</Flex>
							)}
					</>
					<EarSectionSpacer />
					<EarSectionType />
					{earmold && (
						<>
							<EarSectionForm side={NewOrderEarSide.LEFT} />
						</>
					)}
				</Flex>
			)}
		</Flex>
	);
};

const OrdersNewPageChooseSetupLayout = () => {
	const {
		earprintShippingType,
		commentary,
		setCommentary,
		setAdditionalFiles,
		additionalFiles,
		copyMostFulfilled,
	} = useNewOrderStore();
	const toast = useSimpleToast();

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<Flex direction="column" alignItems="center" gap={6}>
					<PageTitle>Oreille(s) concernée(s)</PageTitle>
				</Flex>

				<Grid
					gridTemplateColumns="1fr 1fr"
					alignItems="flex-start"
					w="min(1600px,90vw)"
					justifyContent="space-between"
					gap={4}
				>
					<EarSectionRight />
					<EarSectionLeft />
				</Grid>

				<Button w="fit-content" variant="outline" onClick={copyMostFulfilled}>
					<CopyIcon mr={2} />
					Copier à l'identique
				</Button>

				<Flex direction="column" w="100%" gap={4}>
					<Text lineHeight="21px" fontSize="16px" fontWeight="600">
						Commentaire
					</Text>
					<Textarea
						defaultValue={commentary}
						onChange={(evt) => setCommentary(evt.target.value)}
					/>
				</Flex>

				<Flex direction="column" w="100%">
					<Flex gap={4}>
						<FileInputButton
							name={"additionalFiles"}
							maxNumberOfFiles={Number.POSITIVE_INFINITY}
							onFileChange={async (files: File[] | null) => {
								if (files === null) {
									return;
								}
								try {
									const savedFiles = await Promise.all(
										files?.map((file) =>
											createFile({ file, fileName: undefined }),
										),
									);
									setAdditionalFiles(savedFiles);
								} catch (err) {
									toast.error("Fichier", "Erreur lors de l'ajout de fichier");
								}
							}}
						/>
						{Array.isArray(additionalFiles) && additionalFiles.length > 0 && (
							<Button variant="outline" onClick={() => setAdditionalFiles([])}>
								Supprimer{" "}
							</Button>
						)}
					</Flex>
					<Flex direction="column">
						{additionalFiles?.map((additionalFile) => (
							<Box key={additionalFile.id}>{additionalFile.fileName}</Box>
						))}
					</Flex>
				</Flex>

				<OrdersNewPageBaseLayoutButton
					validateTooltip={(isValid: boolean) => {
						if (isValid) return undefined;
						if (earprintShippingType === NewOrderEarprintShippingType.DIGITAL) {
							return "Le formulaire n'est pas valide. Vérifier que tous les champs sont remplis pour les côtés sélectionnés et que les fichiers numériques ont été ajoutés.";
						}
						return "Le formulaire n'est pas valide. Vérifier que tous les champs sont remplis pour les côtés sélectionnés.";
					}}
				/>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const OrdersNewPageChooseShippingLayout = () => {
	const { center, date, setDate } = useNewOrderStore();
	const { data: refreshedCenter } = useCenter(center?.id);
	const { data: adminSettings } = useAdminSettings();

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>Détails de livraison</PageTitle>

				<Box w="600px" border="solid 1px" borderColor="gray.200" p={4}>
					<Text lineHeight="21px" fontSize="16px" fontWeight="600">
						Adresse de facturation
					</Text>
					{refreshedCenter?.enterprise?.address && (
						<>
							<Text lineHeight="21px" fontSize="16px" fontWeight="300">
								{refreshedCenter?.enterprise?.name}
							</Text>
							<Text lineHeight="21px" fontSize="16px" fontWeight="300">
								{refreshedCenter?.enterprise?.address?.streetNumber}{" "}
								{refreshedCenter?.enterprise?.address?.streetName}
							</Text>
							<Text
								lineHeight="21px"
								fontSize="16px"
								fontWeight="300"
								textTransform="uppercase"
							>
								{refreshedCenter?.enterprise?.address?.postalCode}{" "}
								{refreshedCenter?.enterprise?.address?.city}
							</Text>
						</>
					)}
					{!refreshedCenter?.enterprise?.address && (
						<Text lineHeight="21px" fontSize="16px" fontWeight="100">
							Ce centre n'a pas d'address de facturation
						</Text>
					)}
				</Box>

				<Box w="600px" border="solid 1px" borderColor="gray.200" p={4}>
					<Text lineHeight="21px" fontSize="16px" fontWeight="600">
						Adresse de livraison
					</Text>
					{refreshedCenter?.shippingAddress && (
						<>
							<Text lineHeight="21px" fontSize="16px" fontWeight="300">
								{refreshedCenter.name}
							</Text>
							<Text lineHeight="21px" fontSize="16px" fontWeight="300">
								{refreshedCenter.shippingAddress?.streetNumber}{" "}
								{refreshedCenter.shippingAddress?.streetName}
							</Text>
							<Text
								lineHeight="21px"
								fontSize="16px"
								fontWeight="300"
								textTransform="uppercase"
							>
								{refreshedCenter.shippingAddress?.postalCode}{" "}
								{refreshedCenter.shippingAddress.city}
							</Text>
						</>
					)}
					{!refreshedCenter?.shippingAddress && (
						<Text lineHeight="21px" fontSize="16px" fontWeight="100">
							Ce centre n'a pas d'address de facturation
						</Text>
					)}
				</Box>

				<InputSingleDatePicker
					placeholder="Sélectionner une date"
					date={date}
					setDate={(date?: Date) => date && setDate(date)}
					disableStrategy={() => {
						const ret: Matcher[] = [{ dayOfWeek: [0, 6] }];
						if (adminSettings) {
							const shippingAfter = adminSettings.shippingAfter;
							if (typeof shippingAfter !== "string") {
								return ret;
							}
							const regex = new RegExp(
								"^P([0-9]{1,4})-([0-9]{1,2})-([0-9]{1,2})T([0-9]{1,2}):([0-9]{1,2}):([0-9]{1,2})$",
							);
							const match = regex.exec(shippingAfter);
							if (match === null) {
								return ret;
							}
							ret.push({
								before: add(new Date(), {
									years: Number.parseInt(match[1]),
									months: Number.parseInt(match[2]),
									days: Number.parseInt(match[3]),
									hours: Number.parseInt(match[4]),
									minutes: Number.parseInt(match[5]),
									seconds: Number.parseInt(match[6]),
								}),
							});
							return ret;
						}
						return ret;
					}}
				/>

				<OrdersNewPageBaseLayoutButton />
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const OrdersNewPageValidationLayout = () => {
	const {
		earmold,
		leftEarForm,
		rightEarForm,
		commentary,
		patient,
		center,
		additionalFiles,
		hasLeftEar,
		hasRightEar,
		saveDraft,
		command,
	} = useNewOrderStore();
	const user = useConnectedUser();
	const userFeedbackDisclosure = useDisclosure();
	const toast = useSimpleToast();
	const isValidating = useRef(false);
	const { mutate: mutateSaveDraft, isLoading: isSavingDraft } = useMutation(
		saveDraft,
		{
			onSuccess: async () => {
				userFeedbackDisclosure.onOpen();
				toast.success("Commande", "La commande est validée");
				await queryClient.invalidateQueries();
			},
			onError: () => {
				toast.error("Commande", "La commande n'a pas pu être validée");
			},
		},
	);
	const creatorSelectionModal = useDisclosure();
	const navigate = useNavigate();
	const usersPaginated = useCenterUsersPaginated({ id: center?.id })(1, 1000);
	const { creator, setCreator, updating } = useNewOrderStore();
	const queryClient = useQueryClient();
	const { printPageCommandPurchaseOrder } =
		usePrintPageCommandPurchaseOrderFile({ id: command?.id ?? null });

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<Flex direction="row" gap={4}>
					<Flex direction="column" gap={4} w="max(600px, 46vw)">
						<Flex direction="row" gap={4}>
							{hasRightEar && (
								<Flex w="max(300px,23vw)">
									<RightEarCard earmold={earmold} form={rightEarForm} />
								</Flex>
							)}
							{hasLeftEar && (
								<Flex w="max(300px,23vw)">
									<LeftEarCard earmold={earmold} form={leftEarForm} />
								</Flex>
							)}
						</Flex>
						<Flex>
							<DetailCard
								comment={commentary}
								documents={[...(additionalFiles ?? [])].filter(
									(e) => e !== undefined,
								)}
							/>
						</Flex>
					</Flex>
					<Flex direction="column" w="max(300px,23vw)" gap={4}>
						<PatientCard patient={patient} />
						<CenterCard center={center} />
					</Flex>
				</Flex>

				<OrdersNewPageBaseLayoutButton
					validateLabel={"Valider la commande"}
					onValidate={() => {
						if (isValidating.current === false) {
							isValidating.current = true;
							if (user?.isSuperAdmin) {
								if (updating) {
									mutateSaveDraft({
										status: CommandStatus.WAITING_FOR_RECEPTION,
										log: true,
									});
								} else {
									mutateSaveDraft({
										status: CommandStatus.WAITING_FOR_RECEPTION,
									});
								}
							} else {
								// 1/ Flow is different for non-admin users.
								// They have to specify who is the creator.
								creatorSelectionModal.onOpen();
							}
						}
					}}
				/>

				<SimpleModal
					isOpen={creatorSelectionModal.isOpen}
					onClose={creatorSelectionModal.onClose}
					header={false}
					w={"700px"}
					abortText="Retour"
					acceptText="Valider"
					isPending={isSavingDraft}
					onClick={() => {
						if (creator !== undefined && !isSavingDraft) {
							mutateSaveDraft({ status: CommandStatus.WAITING_FOR_RECEPTION });
						}
					}}
				>
					<Flex direction="column">
						<Text
							mb={8}
							textAlign={"center"}
							lineHeight="36px"
							fontSize="24px"
							fontWeight="600"
						>
							Qui saisit la commande ?
						</Text>

						<Box w="100%">
							<SelectUserCircle
								users={usersPaginated ? usersPaginated.data : []}
								userSelected={creator}
								setUserSelected={setCreator}
								role
							/>
						</Box>
					</Flex>
				</SimpleModal>

				<SimpleSuccessFeedbackModal
					onContinue={async () => {
						userFeedbackDisclosure.onClose();
						await queryClient.invalidateQueries();
						navigate("/orders");
					}}
					onPrint={() => {
						printPageCommandPurchaseOrder();
					}}
					isOpen={userFeedbackDisclosure.isOpen}
				/>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const OrdersNewPageChooseRenewReasonLayout = () => {
	const reasonOptions = [
		{ label: "Renouvellement", value: NewOrderRenewReason.RENEW },
		{
			label: "Changement de type d'embout",
			value: NewOrderRenewReason.CHANGE_EARMOLD_TYPE,
		},
		{
			label: "Changement de caractéristiques",
			value: NewOrderRenewReason.CHANGE_CARACTERISTICS,
		},
		{ label: "Suite à une casse", value: NewOrderRenewReason.BROKEN },
		{ label: "Suite à une perte", value: NewOrderRenewReason.LOST },
		{ label: "Autre", value: NewOrderRenewReason.OTHER },
	];
	const { renewReason, setRenewReason } = useNewOrderStore();

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>De quel motif s'agit-il ?</PageTitle>

				<Flex direction="column" gap={4}>
					{reasonOptions &&
						reasonOptions.map((reasonOption, idx) => (
							<CardSelectable
								w="40rem"
								key={idx}
								title={reasonOption.label}
								activated={reasonOption.value === renewReason}
								onClick={() => setRenewReason(reasonOption.value)}
								inline
							></CardSelectable>
						))}
				</Flex>

				<OrdersNewPageBaseLayoutButton />
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

const OrdersNewPageChooseRenewCommandsLayout = () => {
	const { goNextStep, isUpdate, fromCommand, reworkCommand } =
		useNewOrderStore();

	return (
		<OrdersNewPageBaseLayout>
			<Flex direction="column" gap={12} alignItems="center">
				<PageTitle>Oreille(s) concernée(s)</PageTitle>

				<Grid
					gridTemplateColumns="1fr 1fr"
					alignItems="flex-start"
					w="min(1600px,90vw)"
					justifyContent="space-between"
					gap={4}
				>
					<EarSectionPreviousCommandRight />
					<EarSectionPreviousCommandLeft />
				</Grid>

				<OrdersNewPageBaseLayoutButton
					onValidate={() => {
						if (!isUpdate && reworkCommand) {
							fromCommand(reworkCommand);
						}
						goNextStep();
					}}
				/>
			</Flex>
		</OrdersNewPageBaseLayout>
	);
};

/**
 * Switch between new order steps.
 */
const OrdersNewPage = () => {
	const { getStep, reload } = useNewOrderStore();

	useEffect(() => {
		// A bit brutal, but fix some issues related to draft undefined values not being saved
		reload();
	}, []);

	const step = getStep();
	switch (step) {
		case NewOrderStep.CHOOSE_AUDIO_RESPONSIBLE:
			return <OrdersNewPageChooseAudioResponsibleLayout />;
		case NewOrderStep.CHOOSE_PATIENT:
			return <OrdersNewPageChoosePatientLayout />;
		case NewOrderStep.CHOOSE_COMMAND_TYPE:
			return <OrdersNewPageChooseCommandTypeLayout />;
		case NewOrderStep.CHOOSE_EARPRINT_SHIPPING_TYPE:
			return <OrdersNewPageChooseEarprintShippingTypeLayout />;
		case NewOrderStep.CHOOSE_SETUP:
			return <OrdersNewPageChooseSetupLayout />;
		case NewOrderStep.CHOOSE_SHIPPING:
			return <OrdersNewPageChooseShippingLayout />;
		case NewOrderStep.VALIDATION:
			return <OrdersNewPageValidationLayout />;
		case NewOrderStep.CHOOSE_RENEW_REASON:
			return <OrdersNewPageChooseRenewReasonLayout />;
		case NewOrderStep.CHOOSE_RENEW_COMMANDS:
			return <OrdersNewPageChooseRenewCommandsLayout />;
		case NewOrderStep.CHOOSE_RENEW_SETUP:
			return <OrdersNewPageChooseSetupLayout />;
		default:
			throw new Error(step satisfies never);
	}
};

export default OrdersNewPage;
